import { useCallback } from 'react'

import { CONTAINER_MAX_WIDTH } from '@/config/constants'
import { userKey } from '@/hooks/keyUtils'
import useFeatureFlags from '@/hooks/useFeatureFlags'
import useServiceStatus from '@/hooks/useServiceStatus'
import AppBar from '@/pages/projectList/components/AppBar'
import Footer from '@/pages/projectList/components/Footer'
import NotificationStrip from '@/pages/projectList/components/NotificationStrip'
import useUser from '@/pages/projectList/hooks/useUser'
import { toaster } from '@/system/toaster'
import { Box, Button, Circle, Flex, Icon, Text, VStack } from '@chakra-ui/react'
import {
  ACCOUNT_SETTINGS_TABS,
  AccountSettingsNotification,
  AccountSettingsProvider,
  AccountSettingsTab,
} from '@infraloop/account-settings'
import { Divider } from '@infraloop/common'
import { useTranslation } from '@infraloop/i18n'
import { ProductType, SubscriptionType } from '@infraloop/services'
import { useQueryClient } from '@tanstack/react-query'
import { LuArrowLeft, LuLayoutDashboard } from 'react-icons/lu'
import { Link, Outlet, useMatches, useNavigate } from 'react-router'

/**
 * Hatsuly's account-settings shell: the app bar, a centered narrow card with a
 * left rail of tabs, and the active tab body (from the shared package) in the
 * content pane. Distinct from the platform's full-height sidebar layout.
 */
const UserAccount = () => {
  // Hooks
  const navigate = useNavigate()
  const matches = useMatches()
  const { t } = useTranslation()

  // Data
  const { user, isLoadingUser } = useUser()
  const { featureFlags } = useFeatureFlags()
  const { serviceStatus } = useServiceStatus()
  const queryClient = useQueryClient()

  // Derived state
  const notification = serviceStatus?.notification?.enabled ? serviceStatus.notification : undefined
  const hasDeprecatedSubscription =
    user?.subscriptions?.type === SubscriptionType.Base || user?.subscriptions?.type === SubscriptionType.Features
  const isUserProfileLoaded = Boolean(user) && !isLoadingUser
  const isSubscriptionValid = Boolean(
    user?.owned_products.includes(ProductType.Base) && user?.owned_products.includes(ProductType.Markly),
  )
  // Wait for the profile before deciding — while `user` is undefined `hasDeprecatedSubscription`
  // reads false, and flags load first (no token), so the tab would flash for deprecated users.
  const showBilling =
    user !== undefined && Boolean(featureFlags?.selfManagedSubscriptions.enabled) && hasDeprecatedSubscription === false
  const visibleTabs = ACCOUNT_SETTINGS_TABS.filter((tab) => tab.id !== AccountSettingsTab.Billing || showBilling)

  // Seam callbacks — a save/activation refreshes the cached profile; toasts go through hatsuly's toaster
  const updateUserProfile = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: userKey() })
  }, [queryClient])
  const notify = useCallback(
    ({ status, description }: AccountSettingsNotification) => toaster[status]({ description }),
    [],
  )

  const currentPage = matches.find((match) =>
    Object.values(AccountSettingsTab).includes(match.id as AccountSettingsTab),
  )?.id as AccountSettingsTab
  const selectedPage = currentPage || AccountSettingsTab.Account
  const activeTab = ACCOUNT_SETTINGS_TABS.find((tab) => tab.id === selectedPage)

  /**
   * Navigate to a tab's route within the account area.
   * @param tab Tab to open.
   */
  const goTo = useCallback(
    (tab: AccountSettingsTab) => {
      void navigate(`/user/${tab}`)
    },
    [navigate],
  )

  return (
    <Flex direction="column" minH="100vh" bg="#F6F8FB">
      {notification && <NotificationStrip notification={notification} />}
      <AppBar />
      <Box as="main" flex="1" w="100%" maxW={CONTAINER_MAX_WIDTH} mx="auto" px="28px" pt="34px" pb="64px">
        <Text as="h1" fontSize="24px" fontWeight="bold" color="gray.800" mb="18px">
          {t('ui.accountSettings')}
        </Text>

        <Flex
          align="stretch"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="14px"
          boxShadow="0 1px 3px rgba(16,24,40,.06)"
          overflow="hidden"
          minH="420px"
        >
          {/* Left rail */}
          <VStack
            as="aside"
            w="260px"
            flexShrink={0}
            align="stretch"
            gap="3px"
            p="12px"
            borderRight="1px solid"
            borderColor="gray.200"
          >
            <Button
              variant="plain"
              asChild
              h="auto"
              justifyContent="flex-start"
              gap="9px"
              px="12px"
              py="9px"
              fontSize="13px"
              fontWeight="semibold"
              color="gray.800"
              _hover={{ bg: 'gray.100' }}
              data-testid="account-back-to-dashboard"
            >
              <Link to="/">
                <Icon as={LuArrowLeft} boxSize="15px" color="gray.500" />
                <Icon as={LuLayoutDashboard} boxSize="16px" color="gray.500" />
                {t('userAccount.backToDashboard')}
              </Link>
            </Button>

            <Divider />

            {visibleTabs.map((tab) => {
              const isActive = tab.id === selectedPage
              return (
                <Button
                  key={tab.id}
                  variant="plain"
                  h="auto"
                  justifyContent="flex-start"
                  gap="11px"
                  px="12px"
                  py="9px"
                  borderRadius="8px"
                  fontSize="13px"
                  fontWeight={isActive ? 'semibold' : 'medium'}
                  color={isActive ? 'primary.700' : 'gray.600'}
                  bg={isActive ? 'primary.50' : 'transparent'}
                  _hover={{ bg: isActive ? 'primary.50' : 'gray.100' }}
                  onClick={() => goTo(tab.id)}
                  data-testid={`account-tab-${tab.id}`}
                >
                  <Icon as={tab.icon} boxSize="17px" color={isActive ? 'primary.600' : 'gray.400'} />
                  {t(tab.labelKey)}
                  {tab.id === AccountSettingsTab.Billing && !isSubscriptionValid && (
                    <Circle size={5} bg="orange.500" color="white" fontWeight="bold" ml="auto">
                      !
                    </Circle>
                  )}
                </Button>
              )
            })}
          </VStack>

          {/* Content pane */}
          <Box flex="1" minW={0} bg="gray.50" p="24px">
            <Text mb="18px" fontSize="17px" fontWeight="bold" color="gray.800">
              {activeTab ? t(activeTab.labelKey) : ''}
            </Text>
            <AccountSettingsProvider
              userProfile={user ?? null}
              isUserProfileLoaded={isUserProfileLoaded}
              featureFlags={featureFlags}
              isSubscriptionValid={isSubscriptionValid}
              hasDeprecatedSubscription={hasDeprecatedSubscription}
              updateUserProfile={updateUserProfile}
              notify={notify}
            >
              <Outlet />
            </AccountSettingsProvider>
          </Box>
        </Flex>
      </Box>
      <Footer />
    </Flex>
  )
}

export default UserAccount
